import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck, Shield, ExternalLink, Store,
  Clock, X, CheckCircle2, AlertTriangle,
} from "lucide-react";
import type { DecisionOption, CoverageState } from "@/lib/decisionEngine";
import { captureIntent, addPendingPurchase } from "@/lib/intentCapture";
import { getRoutes, buildOutboundUrl, type SellerRoute } from "@/lib/sellerRouting";
import { usePlan, canUseFeatureForPlan } from "@/hooks/usePlan";
import { loadMemory } from "@/lib/userMemory";
import { useAuth } from "@/contexts/AuthContext";
import { logRouteClick } from "@/lib/cloudServices";
import { toast } from "@/hooks/use-toast";
import UpgradeModal from "./UpgradeModal";
import { trackRouteClick } from "@/lib/behaviorTracking";

const trustConfig = {
  high: { icon: ShieldCheck, label: "High trust", className: "text-score-high" },
  medium: { icon: Shield, label: "Moderate trust", className: "text-score-mid" },
  low: { icon: AlertTriangle, label: "Low trust", className: "text-score-low" },
};

type Step = "routes" | "confirm";

interface Props {
  option: DecisionOption;
  coverage: CoverageState;
  query: string;
  open: boolean;
  onClose: () => void;
}

export default function BuyFlowModal({ option, coverage, query, open, onClose }: Props) {
  const [step, setStep] = useState<Step>("routes");
  const [selectedRoute, setSelectedRoute] = useState<SellerRoute | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const isDemo = coverage === "demo";
  const mem = loadMemory();
  const { user } = useAuth();
  const { plan } = usePlan();
  const routes = getRoutes(option.productName, mem.preferredPriority);

  const handleSelectRoute = (route: SellerRoute) => {
    if (!canUseFeatureForPlan(plan, "save") && !route.recommended) {
      setUpgradeOpen(true);
      return;
    }
    captureIntent({
      type: "select_route",
      productName: option.productName,
      category: "",
      priceRange: option.price,
      selectedSeller: route.name,
      selectedRoute: route.tag || route.name,
      query,
    });
    setSelectedRoute(route);
    setStep("confirm");
  };

  const handleConfirmRedirect = () => {
    if (!selectedRoute) return;
    captureIntent({
      type: "redirect",
      productName: option.productName,
      category: "",
      priceRange: option.price,
      selectedSeller: selectedRoute.name,
      selectedRoute: selectedRoute.tag || selectedRoute.name,
      query,
    });
    addPendingPurchase({
      productName: option.productName,
      price: selectedRoute.price,
      seller: selectedRoute.name,
      deliveryTime: selectedRoute.deliveryTime,
    });

    // Log to cloud
    if (user) {
      logRouteClick({
        userId: user.id,
        productName: option.productName,
        sellerName: selectedRoute.name,
        routeType: selectedRoute.routeType,
        recommended: !!selectedRoute.recommended,
        category: "",
        market: "",
        query,
      });
    }

    const url = buildOutboundUrl(selectedRoute);
    trackRouteClick(option.productName, selectedRoute.name, selectedRoute.routeType, user?.id);
    window.open(url, "_blank", "noopener,noreferrer");

    toast({
      title: "Opening route",
      description: `Redirecting to ${selectedRoute.name}.`,
    });
    onClose();
    setStep("routes");
    setSelectedRoute(null);
  };

  const handleBack = () => { setStep("routes"); setSelectedRoute(null); };
  const handleClose = () => { onClose(); setStep("routes"); setSelectedRoute(null); };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="w-full max-w-md bg-card border border-border rounded-t-2xl sm:rounded-2xl p-6 max-h-[85vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="font-display font-bold text-foreground text-lg">
                {step === "routes" ? "Best place to buy" : "Confirm route"}
              </h3>
              <p className="text-xs text-muted-foreground font-body mt-0.5">{option.productName}</p>
            </div>
            <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-muted/50 transition-colors">
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>

          <AnimatePresence mode="wait">
            {step === "routes" && (
              <motion.div key="routes" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, x: -20 }} className="space-y-3">
                <p className="text-xs text-muted-foreground font-body mb-4">
                  Ranked by trust, delivery, and value.
                </p>

                {routes.map((route, i) => {
                  const Trust = trustConfig[route.trust];
                  const TIcon = Trust.icon;
                  const isLocked = !canUseFeatureForPlan(plan, "save") && !route.recommended && i > 0;
                  const isLowConfidence = route.trustScore < 70;

                  return (
                    <button
                      key={route.name}
                      onClick={() => !isLocked && handleSelectRoute(route)}
                      className={`w-full text-left rounded-xl border p-4 transition-all ${
                        route.recommended
                          ? "border-primary/25 bg-primary/5 hover:border-primary/40"
                          : isLocked
                          ? "border-border/30 bg-muted/10 opacity-60"
                          : "border-border/50 bg-card hover:border-border"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Store className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="text-sm font-body font-semibold text-foreground">{route.name}</span>
                          {route.recommended && !isLowConfidence && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/15 text-primary font-body font-medium">Best place to buy</span>
                          )}
                          {route.recommended && isLowConfidence && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-score-mid/15 text-score-mid font-body font-medium">Verify before purchase</span>
                          )}
                        </div>
                        {route.tag && !route.recommended && (
                          <span className="text-[10px] text-muted-foreground font-body">{route.tag}</span>
                        )}
                      </div>

                      {route.routeType && (
                        <p className="text-[10px] text-primary/60 font-body mb-1.5">{route.routeType}</p>
                      )}

                      <div className="flex items-center gap-4 text-xs font-body mb-2">
                        <span className="text-foreground font-semibold">{route.price}</span>
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />{route.deliveryTime}
                        </span>
                        <span className={`flex items-center gap-1 ${Trust.className}`}>
                          <TIcon className="w-3 h-3" />{Trust.label}
                        </span>
                      </div>

                      <p className="text-[11px] text-muted-foreground font-body">{route.tradeoff}</p>

                      {isLocked && (
                        <p className="text-[10px] text-primary/60 font-body mt-2">Available in Pro</p>
                      )}
                    </button>
                  );
                })}

                {isDemo && (
                  <p className="text-[10px] text-muted-foreground/60 font-body text-center mt-3">
                    Links go to retailer pages. Verify price before purchase.
                  </p>
                )}
              </motion.div>
            )}

            {step === "confirm" && selectedRoute && (
              <motion.div key="confirm" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="space-y-4">
                <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground font-body">Product</span>
                    <span className="text-sm text-foreground font-body font-medium">{option.productName}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground font-body">Price</span>
                    <span className="text-sm text-foreground font-body font-semibold">{selectedRoute.price}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground font-body">Delivery</span>
                    <span className="text-sm text-foreground font-body">{selectedRoute.deliveryTime}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground font-body">Route</span>
                    <span className="text-sm text-foreground font-body">{selectedRoute.name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground font-body">Trust</span>
                    <span className={`text-sm font-body ${trustConfig[selectedRoute.trust].className}`}>
                      {trustConfig[selectedRoute.trust].label}
                    </span>
                  </div>
                </div>

                <div className="rounded-lg border border-primary/15 bg-primary/5 px-3 py-2.5">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs text-primary font-body font-medium">Why this route</p>
                      <p className="text-[11px] text-muted-foreground font-body mt-0.5">
                        {selectedRoute.recommended
                          ? "Strongest trust with reliable delivery for this product."
                          : selectedRoute.tradeoff}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleBack}
                    className="flex-1 py-2.5 rounded-xl text-sm font-body text-muted-foreground hover:text-foreground border border-border hover:border-border/80 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleConfirmRedirect}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-body font-medium bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
                  >
                    Continue to route
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                </div>

                <p className="text-[10px] text-muted-foreground/50 font-body text-center">
                  You will be redirected to {selectedRoute.name}.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>

      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} requiredFeature="Full route comparison" />
    </AnimatePresence>
  );
}
